import { isNativePlatform } from './platform';

/**
 * Cross-platform geolocation.
 *
 * Web: wraps navigator.geolocation (unchanged behavior).
 * Native (Capacitor): uses @capacitor/geolocation, which handles the Android/iOS
 * runtime permission prompt and reads GPS through the native APIs (the WebView's
 * navigator.geolocation is unreliable inside Capacitor).
 *
 * The returned shape and thrown-error `.code` values mirror the web
 * GeolocationPosition / GeolocationPositionError so existing call-site error
 * handling (code 1 = denied, 2 = unavailable, 3 = timeout) keeps working.
 */

export interface NormalizedPosition {
  coords: { latitude: number; longitude: number; accuracy: number };
  timestamp: number;
}

export interface PositionOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

function codedError(message: string, code: 1 | 2 | 3): Error {
  const e: any = new Error(message);
  e.code = code;
  return e;
}

export async function getCurrentPosition(
  options: PositionOptions = {}
): Promise<NormalizedPosition> {
  const enableHighAccuracy = options.enableHighAccuracy ?? true;
  const timeout = options.timeout ?? 15000;
  const maximumAge = options.maximumAge ?? 0;

  if (isNativePlatform()) {
    const { Geolocation } = await import('@capacitor/geolocation');

    // Ensure runtime permission (Android/iOS prompt).
    try {
      const status = await Geolocation.checkPermissions();
      const granted = status.location === 'granted' || status.coarseLocation === 'granted';
      if (!granted) {
        const req = await Geolocation.requestPermissions();
        const reqGranted = req.location === 'granted' || req.coarseLocation === 'granted';
        if (!reqGranted) {
          throw codedError('Location permission denied', 1);
        }
      }
    } catch (permErr: any) {
      if (permErr?.code === 1) throw permErr;
      // checkPermissions/requestPermissions unsupported on some targets — fall through and try to read.
    }

    try {
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy,
        timeout,
        maximumAge,
      });
      return {
        coords: {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        },
        timestamp: pos.timestamp,
      };
    } catch (err: any) {
      const msg = String(err?.message || '').toLowerCase();
      if (msg.includes('denied') || msg.includes('permission')) {
        throw codedError(err?.message || 'Location permission denied', 1);
      }
      if (msg.includes('timeout') || msg.includes('timed out')) {
        throw codedError(err?.message || 'Location request timed out', 3);
      }
      throw codedError(err?.message || 'Unable to determine your location', 2);
    }
  }

  // Web.
  return new Promise<NormalizedPosition>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(codedError('Geolocation is not supported by this device.', 2));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          coords: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          },
          timestamp: pos.timestamp,
        }),
      (err) => reject(err),
      { enableHighAccuracy, timeout, maximumAge }
    );
  });
}
